/**
 * chromatic-sort.js
 * Sorts color swatches in SWATCH view into a visually logical chromatic order.
 *
 * Strategy:
 *  1. Parse each swatch's color from data-option-swatch-value (hex or rgb() formats).
 *  2. Convert RGB → HSL. Also compute the raw color "span" (max channel − min channel)
 *     which is a lightness-independent measure of colorfulness.
 *  3. Classify:
 *       Neutral      – span < 30   (grays, black, charcoal, silver, white)
 *       Earth-tone   – warm hue, modest span or low lightness  (brown, tan, beige, cream)
 *       Chromatic    – everything else, ordered by hue band
 *  4. Group order: neutrals (dark→light) → earth-tones (dark→light) →
 *       reds → oranges → yellows → greens → teals → blues → purples → pinks.
 *  5. Within each hue band: primary sort by hue angle, secondary by lightness (dark→light).
 *  6. Image swatches (url(…)) and missing/invalid values go at the very end, preserving
 *     their relative order among themselves.
 *
 * Scope: SWATCH grid view only.
 *   – Does NOT touch list-view order (alphabetical sort in color-list-view.js).
 *   – Does NOT affect variant mapping, selection logic, availability, or add-to-cart.
 *   – Works for both <250 variant (Liquid-rendered) and >250 variant (JS-rendered) paths.
 *   – Clicking any swatch still selects the correct variant because only DOM display order
 *     changes; all data-value / input value attributes are untouched.
 */
(function () {
  'use strict';

  if (window.__chromaticSortInitialized) return;
  window.__chromaticSortInitialized = true;

  // ---------------------------------------------------------------------------
  // Color parsing utilities
  // ---------------------------------------------------------------------------

  /**
   * Parse a CSS color string into {r, g, b} (each 0–255).
   * Handles: #RRGGBB, #RGB, rgb(r,g,b), rgba(r,g,b,a).
   * Returns null for url(), empty, "null", or unrecognised strings.
   */
  function parseRGB(val) {
    if (!val || val === 'null' || val === '') return null;
    if (val.indexOf('url(') === 0) return null;

    // rgb() / rgba()
    var m = val.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
    if (m) return { r: +m[1], g: +m[2], b: +m[3] };

    // #hex
    if (val.charAt(0) === '#') {
      var hex = val.slice(1);
      if (hex.length === 3) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
      if (hex.length === 6) {
        return {
          r: parseInt(hex.slice(0, 2), 16),
          g: parseInt(hex.slice(2, 4), 16),
          b: parseInt(hex.slice(4, 6), 16),
        };
      }
    }

    // bare 6-char hex without # (e.g. "FF5733" stored in metafield without prefix)
    if (/^[0-9a-fA-F]{6}$/.test(val)) {
      return {
        r: parseInt(val.slice(0, 2), 16),
        g: parseInt(val.slice(2, 4), 16),
        b: parseInt(val.slice(4, 6), 16),
      };
    }

    return null;
  }

  /**
   * Convert RGB (each 0–255) to HSL.
   * Returns { h: 0–360, s: 0–100, l: 0–100 }.
   */
  function rgbToHsl(r, g, b) {
    r /= 255; g /= 255; b /= 255;
    var max = Math.max(r, g, b), min = Math.min(r, g, b);
    var h = 0, s = 0;
    var l = (max + min) / 2;

    if (max !== min) {
      var d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      if      (max === r) h = (g - b) / d + (g < b ? 6 : 0);
      else if (max === g) h = (b - r) / d + 2;
      else                h = (r - g) / d + 4;
      h /= 6;
    }

    return { h: h * 360, s: s * 100, l: l * 100 };
  }

  // ---------------------------------------------------------------------------
  // Sort key
  // ---------------------------------------------------------------------------

  /**
   * Returns a [group, secondary, tertiary] tuple for stable chromatic sorting.
   *
   * Groups:
   *   0  – light neutrals (span < 30, l > 50): white → silver → light gray → mid-gray  (light-first)
   *   1  – earth-tones:                          cream → beige → tan → dark brown        (light-first)
   *   2  – reds       (h 345–375° wrapped, i.e. 345–360 + 0–15)
   *   3  – oranges    (h 15–45°)
   *   4  – yellows    (h 45–75°)
   *   5  – greens     (h 75–165°)
   *   6  – teals      (h 165–210°)
   *   7  – blues      (h 210–260°)
   *   8  – purples    (h 260–310°)
   *   9  – pinks      (h 310–345°)
   *  10  – dark neutrals (span < 30, l ≤ 50): dark gray → charcoal → black  (light-first within)
   *  98  – image swatches (url(…)) – near end, stable order via index
   *  99  – missing / unparseable   – at very end
   */
  function sortKey(swatchValue, stableIndex) {
    if (!swatchValue || swatchValue === 'null' || swatchValue === '') return [99, stableIndex, 0];
    if (swatchValue.indexOf('url(') === 0) return [98, stableIndex, 0];

    var rgb = parseRGB(swatchValue);
    if (!rgb) return [99, stableIndex, 0];

    var r = rgb.r, g = rgb.g, b = rgb.b;
    // "span": raw channel spread — a lightness-independent colorfulness measure (0–255)
    var span = Math.max(r, g, b) - Math.min(r, g, b);
    var hsl  = rgbToHsl(r, g, b);
    var h = hsl.h, s = hsl.s, l = hsl.l;

    // ── Neutrals ─────────────────────────────────────────────────────────────
    // Very low span means essentially a gray regardless of perceived hue.
    // Split into two buckets so dark neutrals (blacks/charcoals) appear AFTER all
    // chromatic colors, not before them.
    //   Group  0 (l > 50): white → silver → light gray → mid-gray   (light-first: 100-l)
    //   Group 10 (l ≤ 50): dark gray → charcoal → black             (light-first: 100-l)
    // Using (100 - l) as the secondary key sorts higher-lightness values first.
    if (span < 30) return [l > 50 ? 0 : 10, 100 - l, 0];

    // ── Earth-tones ──────────────────────────────────────────────────────────
    // Light earth-tones (cream, ivory, warm white, light beige):
    //   warm hue 10–80°, high lightness > 78%, modest span < 80.
    // Secondary key (100 - l) sorts lighter shades first, matching the neutral convention.
    if (l > 78 && h >= 10 && h <= 80 && span < 80) return [1, 100 - l, h];

    // Dark/medium earth-tones (brown, sienna, tan, khaki, caramel, copper):
    //   warm hue 10–55°, not fully bright (s < 80%), lightness < 75%.
    if (h >= 10 && h <= 55 && l < 75 && s < 80) return [1, 100 - l, h];

    // ── Chromatic ─────────────────────────────────────────────────────────────
    // Reds: wrap hue so 345–360° and 0–15° form a contiguous band (345–375°).
    if (h < 15 || h >= 345) {
      var adjH = h < 15 ? h + 360 : h; // 0–14 → 360–374; 345–359 → 345–359
      return [2, adjH, l];
    }
    if (h < 45)  return [3, h, l]; // oranges
    if (h < 75)  return [4, h, l]; // yellows / golds
    if (h < 165) return [5, h, l]; // greens
    if (h < 210) return [6, h, l]; // teals / aquas / turquoises
    if (h < 260) return [7, h, l]; // blues / navies
    if (h < 310) return [8, h, l]; // purples / violets
    return [9, h, l];              // pinks / magentas (310–345°)
  }

  /** Lexicographic comparison of two sort-key arrays. */
  function cmp(a, b) {
    for (var i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return a[i] - b[i];
    }
    return 0;
  }

  // ---------------------------------------------------------------------------
  // DOM helpers: extract swatch color value from an element
  // ---------------------------------------------------------------------------

  function swatchColorOf(el) {
    return el ? (el.getAttribute('data-option-swatch-value') || '') : '';
  }

  // ---------------------------------------------------------------------------
  // DOM reorder — Case A: <label> elements that each *contain* their <input>
  // (250-variant renderSwatches() path; labels are direct children of cpOverride)
  // ---------------------------------------------------------------------------

  function sortEmbeddedLabels(container) {
    var labels = [];
    var ch = container.children;
    for (var i = 0; i < ch.length; i++) {
      if (ch[i].tagName === 'LABEL') labels.push(ch[i]);
    }
    if (labels.length < 2) return;

    var keyed = labels.map(function (el, idx) {
      return { el: el, key: sortKey(swatchColorOf(el), idx) };
    });
    keyed.sort(function (a, b) { return cmp(a.key, b.key); });
    keyed.forEach(function (item) { container.appendChild(item.el); });
  }

  // ---------------------------------------------------------------------------
  // DOM reorder — Case B: alternating <input type="radio"> + <label for="…"> siblings
  // (<250 variant product-variant-options.liquid path; inside fieldset or .color-swatch-grid-view)
  // ---------------------------------------------------------------------------

  function sortInputLabelPairs(container) {
    var ch = Array.from(container.children);
    var pairs = [];
    var i = 0;

    while (i < ch.length) {
      var child = ch[i];
      if (child.tagName === 'INPUT' && child.type === 'radio' && child.id) {
        var next = ch[i + 1];
        if (next && next.tagName === 'LABEL' && next.getAttribute('for') === child.id) {
          pairs.push({
            input: child,
            label: next,
            key: sortKey(swatchColorOf(next), pairs.length),
          });
          i += 2;
          continue;
        }
      }
      i++;
    }

    if (pairs.length < 2) return;

    // Detach all pairs from the DOM
    pairs.forEach(function (p) { p.input.remove(); p.label.remove(); });

    // Sort
    pairs.sort(function (a, b) { return cmp(a.key, b.key); });

    // Re-insert: place sorted pairs before the first <script> child (if any),
    // which preserves the JSON / init script at the end of the container.
    var ref = container.querySelector(':scope > script') || null;
    pairs.forEach(function (p) {
      if (ref) {
        container.insertBefore(p.input, ref);
        container.insertBefore(p.label, ref);
      } else {
        container.appendChild(p.input);
        container.appendChild(p.label);
      }
    });
  }

  // ---------------------------------------------------------------------------
  // DOM reorder — Case C: <div class="product__color-swatch …"> elements
  // (Collection-page item swatches inside variant-radios.product__color-swatches)
  // ---------------------------------------------------------------------------

  function sortSwatchDivs(container) {
    var divs = [];
    var ch = container.children;
    for (var i = 0; i < ch.length; i++) {
      if (ch[i].tagName === 'DIV' && ch[i].classList.contains('product__color-swatch')) {
        divs.push(ch[i]);
      }
    }
    if (divs.length < 2) return;

    var keyed = divs.map(function (el, idx) {
      return { el: el, key: sortKey(swatchColorOf(el), idx) };
    });
    keyed.sort(function (a, b) { return cmp(a.key, b.key); });

    // Insert before the JSON <script> block (keeps variant data in place)
    var ref = container.querySelector(':scope > script[type="application/json"]') || null;
    keyed.forEach(function (item) {
      if (ref) container.insertBefore(item.el, ref);
      else     container.appendChild(item.el);
    });
  }

  // ---------------------------------------------------------------------------
  // Smart dispatcher: detect structure and apply correct sort
  // ---------------------------------------------------------------------------

  function sortContainer(container) {
    if (!container) return;

    // Case A: 250-variant labels (direct children that are <label> elements)
    var fc = container.firstElementChild;
    if (fc && fc.tagName === 'LABEL' && fc.classList.contains('product__color-swatches--js')) {
      sortEmbeddedLabels(container);
      return;
    }

    // Case B: input + label[for] sibling pairs
    if (container.querySelector(':scope > input[type="radio"]')) {
      sortInputLabelPairs(container);
      return;
    }

    // Case C: collection-page div swatches (handled via sortSwatchDivs directly)
    sortSwatchDivs(container);
  }

  // ---------------------------------------------------------------------------
  // Main: sort all currently-rendered swatch grid views
  // ---------------------------------------------------------------------------

  function sortAllSwatchViews() {
    // 1. Products with >30 colors, <250 variants: .color-swatch-grid-view
    //    (the grid-view may be hidden by default when list view is active — sort anyway
    //     so swatches appear in order when the user switches to swatch view)
    var gridViews = document.querySelectorAll('.color-swatch-grid-view');
    for (var i = 0; i < gridViews.length; i++) {
      sortContainer(gridViews[i]);
    }

    // 2. Products with ≤30 colors (detail page): fieldset.product-form__input_color
    //    The input+label pairs live directly inside the fieldset (no grid-view wrapper).
    var colorFieldsets = document.querySelectorAll('fieldset.product-form__input_color');
    for (var j = 0; j < colorFieldsets.length; j++) {
      // Skip fieldsets that already contain a grid-view (handled above)
      if (!colorFieldsets[j].querySelector('.color-swatch-grid-view')) {
        sortContainer(colorFieldsets[j]);
      }
    }

    // 3. Collection-page item swatches: .product__color-swatches (variant-radios wrapper)
    var itemContainers = document.querySelectorAll('.product__color-swatches');
    for (var k = 0; k < itemContainers.length; k++) {
      sortSwatchDivs(itemContainers[k]);
    }
  }

  // ---------------------------------------------------------------------------
  // 250-variant hook: watch #color-picker-override for dynamically inserted swatches.
  // renderSwatches() in color-swatches.liquid calls cpOverride.innerHTML = … after
  // the Storefront API fetch resolves, so we use a MutationObserver to detect that.
  // ---------------------------------------------------------------------------

  function watchForColorPickerOverride() {
    var cpOverride = document.getElementById('color-picker-override');
    if (!cpOverride) return;

    var observer = new MutationObserver(function () {
      // Fire once labels have been inserted (the <p>Loading…</p> placeholder is replaced)
      var firstLabel = cpOverride.querySelector(':scope > label.product__color-swatches--js');
      if (firstLabel) {
        sortEmbeddedLabels(cpOverride);
        observer.disconnect();
      }
    });

    observer.observe(cpOverride, { childList: true });
  }

  // ---------------------------------------------------------------------------
  // Init
  // ---------------------------------------------------------------------------

  function init() {
    sortAllSwatchViews();
    watchForColorPickerOverride();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for external callers (e.g. after AJAX section refreshes)
  window.chromaticSortSwatches = sortAllSwatchViews;
}());
